import type { LevelIdentity } from '../model/identity';
import type { SerializedGeneratedLevel } from '../model/level';

/**
 * Typed worker protocol.
 *
 * Messages carry only plain serializable data. Timing of responses can never influence
 * generation output: the worker is a pure function evaluator, and the client discards
 * results whose request id is no longer current.
 */

export interface GenerationRequest {
  readonly type: 'generate';
  readonly requestId: string;
  readonly identity: LevelIdentity;
}

export interface CancelRequest {
  readonly type: 'cancel';
  readonly requestId: string;
}

export type WorkerRequest = GenerationRequest | CancelRequest;

export interface GenerationSuccess {
  readonly type: 'success';
  readonly requestId: string;
  readonly level: SerializedGeneratedLevel;
  /** Wall-clock duration, for developer tooling only. Never fed back into generation. */
  readonly durationMs: number;
}

export interface GenerationFailure {
  readonly type: 'failure';
  readonly requestId: string;
  readonly errorCode: string;
  readonly details?: string;
}

export type WorkerResponse = GenerationSuccess | GenerationFailure;

export function isWorkerResponse(value: unknown): value is WorkerResponse {
  if (typeof value !== 'object' || value === null) return false;
  const message = value as Record<string, unknown>;
  if (typeof message.requestId !== 'string') return false;
  return message.type === 'success' || message.type === 'failure';
}
